/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IOrderDetailsResources } from '../definition-extensions/order-details.ext.props.autogenerated';

export const OrderInfomation: React.FC<IOrderInformation & { resources: IOrderDetailsResources }> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    resources
}) => (
    <>
        <Node {...orderInformationProps}>
            {/* {channelName} */}
            <span className='ms-order-details__order-information-created-date-label'>
                <span className='ms-order-details__order-information-key'>{resources.orderDetailsOrderInformationCreatedDate}</span>
                {createdDate}
            </span>
            {channelAddress}
            <span className='ms-order-details__order-information-order-number-label'>
                <span className='ms-order-details__order-information-key'>{resources.orderDetailsOrderInformationOrderNumber}</span>
                {salesId}
            </span>
            {receiptId}
            {/* {count} */}
            <span className='ms-order-details__order-information-total-label'>
                <span className='ms-order-details__order-information-key'>{resources.orderDetailsOrderInformationTotal}</span>
                {amount}
            </span>
            {/* {channelReferenceId} */}
        </Node>
    </>
);

export const SalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton, errors }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
        {errors}
    </Node>
);

export const GroupDelivery: React.FC<IGroupDelivery & { resources: IOrderDetailsResources }> = ({
    deliveryProps,
    heading,
    count,
    processing,
    resources
}) => (
    <Node {...deliveryProps}>
        <div className='ms-order-details__group-delivery-heading'>
            {heading}
            {count}
        </div>
        <div className='ms-order-details__group-delivery-tracking'>
            <div className='ms-order-details__delivery-processing'>
                <span className='ms-order-details__address-container-key'>{resources.orderDetailsOrderInformationStatus}</span> {processing}
            </div>
        </div>
    </Node>
);

export const Group: React.FC<IGroup & { resources: IOrderDetailsResources }> = ({
    groupProps,
    delivery,
    address,
    salesLinesProps,
    salesLines,
    isCashAndCarryTransaction,
    resources
}) => (
    <Node {...groupProps}>
        {delivery && <GroupDelivery {...{ ...delivery, resources }} />}
        <Node className='ms-order-details__sales-lines_container'>
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map(salesLine => (
                        <React.Fragment key={salesLine.data.salesLine.LineId}>
                            {/* {!isCashAndCarryTransaction && salesLine.salesStatus} */}
                            {salesLine.data.deliveryType === 'ship' && salesLine.data.shipment ? salesLine.trackingInfo : null}
                            <SalesLine {...salesLine} />
                        </React.Fragment>
                    ))}
                </Node>
            )}
            {/* {!delivery.showTimeslot && address}
            {delivery.showTimeslot && <Node {...delivery.pickupProps}>{delivery.pickupDateTimeslot}</Node>} */}
        </Node>
    </Node>
);

export const Groups: React.FC<IGroups & { resources: IOrderDetailsResources }> = ({ groupsProps, groups, resources }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <Group key={index} {...{ ...group, resources }} />
        ))}
    </Node>
);

export const OrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const Address: React.FC<IGroups & { resources: IOrderDetailsResources }> = ({ groups, groupsProps, resources }) => {
    return (
        <Node {...groupsProps} className='ms-order-details__address'>
            <Node className='ms-order-details__address-container'>
                {groups.map((group: IGroup, index: number) => {
                    return (
                        <Node key={index} className='ms-order-details__group'>
                            <div className='ms-order-details__address-line'>{group.address}</div>
                        </Node>
                    );
                })}
            </Node>
        </Node>
    );
};

export const Help: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = ({
    moduleProps,
    viewModes,
    tableViewActions,
    table,
    heading,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    help,
    groups,
    ...props
}) => {
    const resources = props.resources as IOrderDetailsResources;

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            <div className='ms-order-details__header'>
                {/* {orderInfomation && <OrderInfomation {...orderInfomation} />} */}
                {orderInfomation && <OrderInfomation {...{ ...orderInfomation, resources }} />}
                {groups && <Address {...{ ...groups, resources }} />}
                <Node className='ms-order-details__order-summary-container'>{orderSummary && <OrderSummary {...orderSummary} />}</Node>
            </div>
            {viewModes}
            {table}
            {groups && <Groups {...{ ...groups, resources }} />}
            {tableViewActions}
            {help && <Help {...help} />}
        </Module>
    );
};

export default OrderDetailsView;
